import { useState } from 'react';
import { Event, Filter } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';

/**
 * NIP-50 Search Hook with Advanced Filters
 * Search across Nostr content with filtering options
 */

export interface SearchFilters {
    query: string;
    kinds?: number[];          // Filter by event kinds
    authors?: string[];        // Filter by author pubkeys
    since?: number;            // Unix timestamp - from date
    until?: number;            // Unix timestamp - to date
    hashtags?: string[];       // Filter by hashtags
    turkishOnly?: boolean;     // Only Turkish content
    limit?: number;            // Max results
}

import { useGlobalModeration } from './useGlobalModeration';

export const useSearchNostr = () => {
    const [results, setResults] = useState<Event[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const pool = getNostrPool();
    const { blockedEventIds } = useGlobalModeration();

    // Simple search (backwards compatible)
    const search = async (query: string, turkish: boolean = true) => {
        return advancedSearch({
            query,
            turkishOnly: turkish,
            kinds: [1, 30023],
            limit: 30
        });
    };

    // Advanced search with filters
    const advancedSearch = async (filters: SearchFilters) => {
        if (!filters.query.trim()) {
            setResults([]);
            return;
        }

        setIsSearching(true);

        try {
            // NIP-50 compatible relays
            const searchRelays = [
                'wss://relay.nostr.band',
                'wss://nostr.wine',
            ];

            const filter: Filter & { search?: string } = {
                kinds: filters.kinds || [1, 30023],
                search: filters.query,
                limit: filters.limit || 50,
            };

            // Add author filter
            if (filters.authors && filters.authors.length > 0) {
                filter.authors = filters.authors;
            }

            // Add time range filters
            if (filters.since) {
                filter.since = filters.since;
            }
            if (filters.until) {
                filter.until = filters.until;
            }

            // Add hashtag filter (via #t tags)
            if (filters.hashtags && filters.hashtags.length > 0) {
                (filter as any)['#t'] = filters.hashtags;
            }

            const events = await pool.querySync(searchRelays, filter as Filter);

            // Filter for Turkish if requested
            let filteredEvents = events;
            if (filters.turkishOnly) {
                const turkishWords = ['bir', 've', 'bu', 'için', 'ile', 'var', 'gibi', 'ama', 'çok', 'olan', 'olarak', 'da', 'de'];
                const turkishChars = /[çğıöşüÇĞİÖŞÜ]/;

                filteredEvents = events.filter(event => {
                    const contentLower = event.content.toLowerCase();
                    // Check for Turkish characters
                    if (turkishChars.test(event.content)) return true;
                    // Check for common Turkish words
                    return turkishWords.some(word =>
                        contentLower.includes(` ${word} `) ||
                        contentLower.startsWith(`${word} `) ||
                        contentLower.endsWith(` ${word}`)
                    );
                });
            }

            // Global Moderation Filter
            filteredEvents = filteredEvents.filter(event => !blockedEventIds.has(event.id));

            setResults(filteredEvents.sort((a, b) => b.created_at - a.created_at));
        } catch (error) {
            console.error('Search failed:', error);
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const clearResults = () => setResults([]);

    return { results, isSearching, search, advancedSearch, clearResults };
};
